import type { SearchCriteria } from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { SORT_OPTIONS_ENUM } from '@/constants'

const MAX_QUERY_ATTRIBUTE_LENGTH = 256
const MAX_QUERY_LIST_ITEMS = 50

type QueryParamParser = (values: string[]) => string | undefined

const hasControlCharacters = (value: string): boolean =>
  Array.from(value).some((character) => {
    const characterCode = character.charCodeAt(0)
    return characterCode <= 31 || characterCode === 127
  })

const parseEnum =
  (allowedValues: readonly string[]): QueryParamParser =>
  (values) =>
    values.length === 1 && allowedValues.includes(values[0])
      ? values[0]
      : undefined

const parseInteger: QueryParamParser = (values) =>
  values.length === 1 && /^\d{1,10}$/.test(values[0]) ? values[0] : undefined

const parseNumber: QueryParamParser = (values) =>
  values.length === 1 && /^\d{1,10}(?:\.\d{1,10})?$/.test(values[0])
    ? values[0]
    : undefined

const parseBoolean: QueryParamParser = (values) =>
  values.length === 1 && (values[0] === 'true' || values[0] === 'false')
    ? values[0]
    : undefined

const parseNumericList: QueryParamParser = (values) => {
  const items = values.flatMap((value) => value.split(','))
  const value = items.join(',')

  return items.length > 0 &&
    items.length <= MAX_QUERY_LIST_ITEMS &&
    value.length <= MAX_QUERY_ATTRIBUTE_LENGTH &&
    items.every(
      (item) => !hasControlCharacters(item) && /^\d{1,10}$/.test(item),
    )
    ? value
    : undefined
}

// Deliberately exclude free-form text, locations, dates and identifiers.
// SearchCriteria membership alone does not make a value safe for telemetry.
const SEARCH_CRITERIA_QUERY_PARAM_PARSERS = {
  accommodationType: parseEnum([
    'cottages',
    'lodges',
    'holiday-parks',
    'novasol_cottages',
    'accom',
    'boats',
    'boat-holidays',
  ]),
  adult: parseInteger,
  child: parseInteger,
  discounts: parseInteger,
  features: parseNumericList,
  infant: parseInteger,
  maxPrice: parseNumber,
  minPrice: parseNumber,
  nights: parseInteger,
  page: parseInteger,
  pets: parseInteger,
  range: parseInteger,
  showExpandedResults: parseBoolean,
  siteFeatures: parseNumericList,
  size: parseInteger,
  sort: parseEnum(Object.values(SORT_OPTIONS_ENUM)),
  useExpandedCoordinates: parseBoolean,
} satisfies Partial<Record<keyof SearchCriteria, QueryParamParser>>

const DISPLAY_MODE_VALUES = new Set(['LIST', 'MAP'])

export const getSearchQueryAttributes = (
  search: string,
): Record<string, string> => {
  const queryParams = new URLSearchParams(search)
  const attributes: Record<string, string> = {}

  Object.entries(SEARCH_CRITERIA_QUERY_PARAM_PARSERS).forEach(
    ([name, parse]) => {
      const value = parse(queryParams.getAll(name))

      if (value !== undefined) {
        attributes[`query_${name}`] = value
      }
    },
  )

  const displayModes = queryParams.getAll('displayMode')
  if (displayModes.length === 1 && DISPLAY_MODE_VALUES.has(displayModes[0])) {
    attributes.query_displayMode = displayModes[0]
  }

  return attributes
}
